import {
  DatePicker,
  Field,
  Unifree,
  YesObject,
  datepickerToISOFormat
} from '@yes.technology/react-toolkit'
import { useTranslation } from 'react-i18n-lite'
import { MODIFIABLE_FIELDS_DENYLIST } from 'shared/constants'
import formatDate from 'shared/utils/date'
import { AllowedLocales } from 'shared/utils/date/formatDate'
import HtmlEditorField from './HtmlEditorField'

export interface FieldWithColumns extends Field {
  columns?: string[]
  informational?: boolean
}

export type FieldsProps = {
  fields: FieldWithColumns[]
  fieldValues?: Record<string, string | YesObject[]>
  informational: boolean
  onChange: (fieldColumn: string, value: string) => void
  isEditing?: boolean
  enableLabelFading?: boolean
  enableValueFading?: boolean
  htmlEditorFields?: string[]
}

const isUnmodifiableField = (field: Field) =>
  MODIFIABLE_FIELDS_DENYLIST.includes(field.column_json)

const isHtmlEditorField = (columnJson: string, htmlEditorFields?: string[]) =>
  htmlEditorFields?.includes(columnJson) ?? false

const Fields = ({
  fields,
  fieldValues = {},
  informational,
  onChange,
  isEditing,
  enableLabelFading = true,
  enableValueFading = true,
  htmlEditorFields
}: FieldsProps) => {
  const { language } = useTranslation()

  const getFieldAttributeValue = (field: FieldWithColumns) => {
    const fieldAttributeValue =
      informational || field.informational || isUnmodifiableField(field)

    return isEditing
      ? {
          disabled: fieldAttributeValue
        }
      : {
          informational: fieldAttributeValue
        }
  }

  const isFieldEditable = (field: FieldWithColumns) => {
    const fieldAttributeValue =
      informational || field.informational || isUnmodifiableField(field)

    return Boolean(isEditing && !fieldAttributeValue)
  }

  return (
    <div className='row mt-2 mb-2'>
      {fields.map((field) => {
        let displayedValue = fieldValues[field.column_json] || ''
        if (field.type === 'DATETIME' && fieldValues[field.column_json]) {
          displayedValue = formatDate(
            fieldValues[field.column_json] as string,
            language as AllowedLocales
          )
        }
        if (field.type === 'DATETIME' && !informational) {
          return (
            <div
              key={field.uuid}
              className={`${field.columns ? field?.columns?.join(' ') : 'col-md-3 position-relative'}`}
            >
              <DatePicker
                onChange={(dateTime) =>
                  onChange(
                    field.column_json,
                    datepickerToISOFormat(dateTime || '', {
                      locale: language as AllowedLocales,
                      timezone: window.waTimezone
                    }) || ''
                  )
                }
                heightClass='regular'
                format='date-time'
                lang={language}
                value={typeof displayedValue === 'string' ? displayedValue : ''}
                placeholder={field.display_text.des}
                label={field.display_text.des}
                {...getFieldAttributeValue(field)}
              />
            </div>
          )
        }

        if (isHtmlEditorField(field.column_json, htmlEditorFields)) {
          return (
            <div
              key={field.uuid}
              className={`${field.columns ? field?.columns?.join(' ') : 'col-md-3'}`}
            >
              <HtmlEditorField
                field={field}
                value={displayedValue as string}
                isEditing={isEditing}
                isEditable={isFieldEditable(field)}
                {...getFieldAttributeValue(field)}
                onChange={(value) => onChange(field.column_json, value)}
              />
            </div>
          )
        }

        return (
          <div
            key={field.uuid}
            className={`${field.columns ? field?.columns?.join(' ') : 'col-md-3'}`}
          >
            <Unifree
              label={field.display_text.des}
              value={displayedValue as string}
              required
              onChange={(value) => onChange(field.column_json, value)}
              enableLabelFading={
                field?.enable_label_fading ?? enableLabelFading
              }
              enableValueFading={
                field?.enable_value_fading ?? enableValueFading
              }
              {...getFieldAttributeValue(field)}
            />
          </div>
        )
      })}
    </div>
  )
}

export default Fields
